import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginUser, registerUser, getMe } from '../services/api';

const AuthContext = createContext(null);

const USER_CACHE_KEY = 'nr_user';

function clearTokens() {
  localStorage.removeItem('nr_token');
  localStorage.removeItem('nr_refresh_token');
  localStorage.removeItem(USER_CACHE_KEY);
}

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(u) {
  try {
    if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u));
    else localStorage.removeItem(USER_CACHE_KEY);
  } catch {}
}

export function AuthProvider({ children }) {
  // Optimistic hydration: if a token exists, render with the cached user
  // immediately so the UI doesn't flash signed-out during the /me round-trip.
  const [user, setUser] = useState(() => {
    return localStorage.getItem('nr_token') ? readCachedUser() : null;
  });
  const [authLoading, setAuthLoading] = useState(true);

  // On mount: revalidate cached user against the server.
  useEffect(() => {
    const token = localStorage.getItem('nr_token');
    if (!token) {
      setAuthLoading(false);
      return;
    }
    getMe()
      .then((u) => {
        setUser(u);
        writeCachedUser(u);
      })
      .catch((err) => {
        // Only clear the token on 401 (invalid/expired). Network errors and
        // other transient failures during OAuth redirects must not log the user out.
        // The 401 interceptor in api.js will have already attempted a refresh by now.
        if (err?.response?.status === 401) {
          clearTokens();
          setUser(null);
        }
      })
      .finally(() => setAuthLoading(false));
  }, []);

  // Listen for forced logout from the 401 interceptor (refresh token also expired)
  useEffect(() => {
    const handle = () => {
      writeCachedUser(null);
      setUser(null);
    };
    window.addEventListener('nr:logout', handle);
    return () => window.removeEventListener('nr:logout', handle);
  }, []);

  const login = useCallback(async (email, password) => {
    const { access_token, refresh_token, user: u } = await loginUser(email, password);
    localStorage.setItem('nr_token', access_token);
    if (refresh_token) localStorage.setItem('nr_refresh_token', refresh_token);
    setUser(u);
    writeCachedUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, password, playerTag) => {
    const { access_token, refresh_token, user: u } = await registerUser(email, password, playerTag);
    localStorage.setItem('nr_token', access_token);
    if (refresh_token) localStorage.setItem('nr_refresh_token', refresh_token);
    setUser(u);
    writeCachedUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  // Call this after claiming a profile or updating user fields
  const refreshUser = useCallback(() => {
    return getMe().then((u) => {
      setUser(u);
      writeCachedUser(u);
      return u;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, authLoading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
